import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Download, Search } from "lucide-react";
import { api, API, BACKEND_URL } from "../lib/api";

const FILTER_DEFS = [
  ["gender", "Пол", "select", [["male", "Мужской"], ["female", "Женский"], ["na", "Не указан"]]],
  ["age_min", "Возраст от", "number"],
  ["age_max", "Возраст до", "number"],
  ["city", "Город", "text"],
  ["language", "Язык", "select", [["ru", "RU"], ["uz", "UZ"], ["en", "EN"]]],
  ["has_phone", "Есть телефон", "bool"],
  ["has_email", "Есть email", "bool"],
  ["telegram_connected", "Telegram подключён", "bool"],
  ["email_verified", "Email подтверждён", "bool"],
  ["inactive_days", "Не заходил X дней", "number"],
  ["active_within_days", "Был активен за X дней", "number"],
  ["min_visits", "Мин. посещений", "number"],
  ["min_purchases", "Мин. покупок", "number"],
  ["campaign_completed", "Прошёл кампанию", "campaign"],
  ["birthday_within_days", "ДР в ближайшие X дней", "number"],
];
const HIDDEN_CUSTOM_FIELDS = ["favorite_product", "favorite_dish"];

export function FilterBuilder({ filters, setFilters, meta }) {
  const defs = [...FILTER_DEFS, ...(meta?.custom_fields || []).filter((f) => !HIDDEN_CUSTOM_FIELDS.includes(f.key)).map((f) => [`field.${f.key}`, `Поле: ${f.key}`, "customfield", f.values])];
  const upd = (i, patch) => setFilters(filters.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  return (
    <div className="space-y-2" data-testid="filter-builder">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-extrabold uppercase text-gray-400">Фильтры</span>
        <button data-testid="filter-add-btn" className="admin-btn admin-btn-ghost !h-7 text-xs" onClick={() => setFilters([...filters, { field: "gender", value: "female" }])}><Plus size={12} /> Условие</button>
      </div>
      {filters.map((f, i) => {
        const def = defs.find((d) => d[0] === f.field) || defs[0];
        return (
          <div key={i} className="flex gap-2 items-center flex-wrap" data-testid={`filter-row-${i}`}>
            <select className="admin-input !w-52 !h-8 text-xs" value={f.field} onChange={(e) => upd(i, { field: e.target.value, value: "" })}>
              {defs.map((d) => <option key={d[0]} value={d[0]}>{d[1]}</option>)}
            </select>
            {def[2] === "select" && (
              <select className="admin-input !w-36 !h-8 text-xs" value={f.value} onChange={(e) => upd(i, { value: e.target.value })}>
                <option value="">—</option>{def[3].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            )}
            {def[2] === "bool" && (
              <select className="admin-input !w-24 !h-8 text-xs" value={String(f.value)} onChange={(e) => upd(i, { value: e.target.value === "true" })}>
                <option value="true">Да</option><option value="false">Нет</option>
              </select>
            )}
            {def[2] === "number" && <input type="number" className="admin-input !w-24 !h-8 text-xs" value={f.value} onChange={(e) => upd(i, { value: +e.target.value })} />}
            {def[2] === "text" && <input className="admin-input !w-36 !h-8 text-xs" value={f.value} onChange={(e) => upd(i, { value: e.target.value })} />}
            {def[2] === "campaign" && (
              <select className="admin-input !w-44 !h-8 text-xs" value={f.value} onChange={(e) => upd(i, { value: e.target.value })}>
                <option value="">—</option>{(meta?.campaigns || []).map((c) => <option key={c.id} value={c.id}>{c.name?.ru}</option>)}
              </select>
            )}
            {def[2] === "customfield" && (
              <select className="admin-input !w-36 !h-8 text-xs" value={f.value} onChange={(e) => upd(i, { value: e.target.value })}>
                <option value="">—</option>{(def[3] || []).map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            )}
            <button onClick={() => setFilters(filters.filter((_, j) => j !== i))} className="text-red-400"><Trash2 size={14} /></button>
          </div>
        );
      })}
    </div>
  );
}

export default function Customers() {
  const [data, setData] = useState({ total: 0, items: [] });
  const [filters, setFilters] = useState([]);
  const logic = "and";
  const [meta, setMeta] = useState(null);
  const [segments, setSegments] = useState([]);
  const [search, setSearch] = useState("");

  const query = (f = filters) => {
    const all = search ? [...f, { field: "search", value: search }] : f;
    api.post("/admin/customers/query", { filters: all, logic, limit: 100 }).then((r) => setData(r.data));
  };
  useEffect(() => {
    query();
    api.get("/admin/filter-fields").then((r) => setMeta(r.data));
    api.get("/admin/segments").then((r) => setSegments(r.data));
  }, []);

  const exportData = async (format) => {
    try {
      const token = localStorage.getItem("craft_token");
      const res = await fetch(`${API}/admin/customers/export`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ filters, logic, format }),
      });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `customers.${format}`;
      a.click();
    } catch (e) { toast.error("Ошибка экспорта"); }
  };

  const delCustomer = async (c) => {
    if (!window.confirm(`Удалить клиента ${c.first_name || ""} ${c.last_name || ""} из базы? Будут удалены его визиты и заявки. Действие необратимо.`)) return;
    try {
      await api.delete(`/admin/customers/${c.id}`);
      toast.success("Клиент удалён");
      query();
    } catch (e) { toast.error("Ошибка удаления"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-customers">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-display font-black text-2xl">Клиенты <span className="text-sm text-gray-400 font-bold">({data.total})</span></h1>
        <div className="flex gap-2">
          <button data-testid="export-csv-btn" className="admin-btn admin-btn-ghost text-xs" onClick={() => exportData("csv")}><Download size={13} /> CSV</button>
          <button data-testid="export-xlsx-btn" className="admin-btn admin-btn-ghost text-xs" onClick={() => exportData("xlsx")}><Download size={13} /> Excel</button>
        </div>
      </div>
      <div className="bg-white rounded-2xl p-4 border border-gray-100 space-y-3">
        <div className="flex gap-2 flex-wrap items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-3 text-gray-400" />
            <input data-testid="customer-search-input" className="admin-input !pl-8" placeholder="Поиск: имя, телефон, email, telegram, код..." value={search}
              onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && query()} />
          </div>
          <select data-testid="segment-select" className="admin-input !w-52" onChange={(e) => {
            const s = segments.find((x) => x.id === e.target.value);
            if (s) { setFilters(s.filters); query(s.filters); }
          }}>
            <option value="">Сегменты...</option>
            {segments.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <button data-testid="apply-filters-btn" className="admin-btn" onClick={() => query()}>Применить</button>
        </div>
        <FilterBuilder filters={filters} setFilters={setFilters} meta={meta} />
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead><tr className="text-left text-xs text-gray-400 uppercase">
            <th className="px-4 py-3">Имя</th><th>Телефон</th><th>Email</th><th>Telegram</th><th>Пол</th><th>Возраст</th><th>Город</th><th>Визиты</th><th>Активность</th><th></th>
          </tr></thead>
          <tbody>
            {data.items.map((c) => (
              <tr key={c.id} className="border-t border-gray-50 hover:bg-yellow-50/40" data-testid={`customer-row-${c.id}`}>
                <td className="px-4 py-2.5 font-bold"><Link to={`/admin/customers/${c.id}`} className="hover:text-[#FF0000]">{c.first_name || "—"} {c.last_name || ""}</Link></td>
                <td>{c.phone || "—"}</td>
                <td>{c.email || "—"}</td>
                <td>{c.telegram_username ? `@${c.telegram_username}` : "—"}</td>
                <td>{c.gender === "male" ? "М" : c.gender === "female" ? "Ж" : "—"}</td>
                <td>{c.age ?? "—"}</td>
                <td>{c.city || "—"}</td>
                <td>{c.total_visits || 0}</td>
                <td className="text-xs text-gray-400">{c.last_activity?.slice(0, 10)}</td>
                <td className="px-2">
                  <button data-testid={`customer-delete-${c.id}`} title="Удалить клиента" onClick={() => delCustomer(c)}
                    className="p-1.5 rounded-lg bg-gray-100 hover:bg-red-100 text-red-500"><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
